import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { NetValueSettingsStateType, defaultSettings } from "./NetValueSettings";
import WidgetLayout from "components/widgetParts/WidgetLayout";
import WidgetTitle from "components/widgetParts/WidgetTitle";
import ISONumber from "components/formatting/ISONumber";
import { lightModeNamedColors } from "theme/infegyColor";
import { Query } from "system/query/Query";
import { useTranslation } from "react-i18next";
import * as d3 from 'd3';

interface NetValueContentProps {
  fieldLabel: string;
  topValue: number;
  bottomValue: number;
  topLabel: string;
  bottomLabel: string;
  settingsState: NetValueSettingsStateType;
  widgetQueryTitle?: string;
}

export default function NetValueContent(props: NetValueContentProps) {
  const { i18n } = useTranslation();
  const {topValue, bottomValue, topLabel, bottomLabel, widgetQueryTitle, settingsState, fieldLabel} = props;
  const currentState = {...defaultSettings, ...settingsState};

  let netValue = (topValue - bottomValue) / (topValue + bottomValue);

  const portionValue = topValue / (topValue + bottomValue);

  if (currentState.displayType === "count") {
    netValue = topValue - bottomValue;
  }

  if (currentState.displayType === "portion") {
    netValue = portionValue;
  }

  if (!(currentState.displayType === "count")) {
    if (netValue > 0.1) {
      netValue = Math.round(netValue * 100.0);
    } else if (netValue > -0.01) {
      netValue = (Math.round(netValue * 1000.0) / 10);
    } else if (netValue < -0.01) {
      netValue = (Math.round(netValue * 10000.0) / 100);
    }
  }

  let colorMode = currentState?.colorMode || "auto";
  if (colorMode === "auto") {
    if (currentState?.displayType === "portion") {
      colorMode = "gradient";
    } else {
      colorMode = "dominant";
    }
  }

  const topColor = currentState?.topColor || lightModeNamedColors.positive;
  const bottomColor = currentState?.bottomColor || lightModeNamedColors.negative;
  const neutralColor = currentState?.neutralColor || lightModeNamedColors.mixed;

  let color;
  if (colorMode === "gradient") {
    const colorLerp = d3.interpolateRgbBasis([ bottomColor, neutralColor, topColor ]);
    color = colorLerp(portionValue);
  } else if (colorMode === "top") {
    color = topColor;
  } else {
    color = netValue < 0 ? bottomColor : topColor;
    if (netValue === 0) {
      color = neutralColor;
    }
  }

  let activeTooltip;
  switch (currentState.displayType) {
    case "count":
      activeTooltip = (<>(<strong style={{color:topColor}}>{topLabel}</strong>) - (<strong style={{color:bottomColor}}>{bottomLabel}</strong>)</>);
      break;
    case "portion":
      activeTooltip = (<><strong style={{color:topColor}}>{topLabel}</strong> / (<strong style={{color:topColor}}>{topLabel}</strong> + <strong style={{color:bottomColor}}>{bottomLabel}</strong>)</>);
      break;
    default:
      activeTooltip = (<>(<strong style={{color:topColor}}>{topLabel}</strong> - <strong style={{color:bottomColor}}>{bottomLabel}</strong>) / (<strong style={{color:topColor}}>{topLabel}</strong> + <strong style={{color:bottomColor}}>{bottomLabel}</strong>) * <strong>100</strong>%</>);
      break;
  }

  const title = (<>
    <Tooltip followCursor={true} title={activeTooltip}>
      <span>
        {!(settingsState?.displayType === "portion") && <> {i18n.t("net")} </>}
        {fieldLabel}
        {settingsState?.displayType === "portion" && <> {i18n.t("portion")} </>}
        {currentState.displayType === "count" && <> {i18n.t("count")} </>}
        : <strong style={{color: topColor}}>{topLabel}</strong> {i18n.t("vs")} <strong style={{color: bottomColor}}>{bottomLabel}</strong></span>
    </Tooltip>
  </>);

  return (
    <WidgetLayout
      header={
        <WidgetTitle
          alignment={currentState?.widgetTitleAlignment}
          settingsTitle={currentState?.widgetTitle}
          defaultTitle={title}
          customTitle={currentState?.customWidgetTitle}
          widgetQueryTitle={widgetQueryTitle} />
      }>
    <Box sx={{ width: "100%", height: "100%", p: 1 }}>
      <Tooltip
        title={
          <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr" }}>
            <Typography variant="h4" sx={{ mb: 1, fontWeight: "normal" }}>
              {topLabel}:
            </Typography>
            <ISONumber typographyVariant="h4" number={topValue} sx={{ color: topColor, m:0, p:0, ml: 1}} />
            <Typography variant="h4" sx={{ mb: 1, fontWeight: "normal" }}>
              {bottomLabel}:
            </Typography>
            <ISONumber typographyVariant="h4" number={bottomValue} sx={{ color: bottomColor, m:0, p:0, ml: 1 }} />
          </Box>
        }
        followCursor={true}
      >
        <span>
          <ISONumber
            fitToContainer={true}
            typographyVariant="h3"
            sx={{
              alignItems: "center",
              color: color,
              display: "flex",
              minHeight: "1px",
              height: "100%",
              position: "relative",
              cursor: "pointer",
              justifyContent: currentState?.alignment ?? "left"
            }}
            includeDecimals={currentState.displayType === "count" ? false : true}
            prefix={netValue > 0 ? (settingsState?.displayType === "portion" ? "" : "+") : ""}
            postfix={currentState.displayType === "count" ? undefined : "%"}
            number={netValue}
          />
        </span>
      </Tooltip>
    </Box>
  </WidgetLayout>
  )
}